import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "./contexts/LanguageContext";
import Layout from "./components/Layout";
import SplitterLanding from "./pages/SplitterLanding";
import RouteSkeleton from "./components/RouteSkeleton";

// Public pages (lazy)
const Services = lazy(() => import("./pages/Services"));
const About = lazy(() => import("./pages/About"));
const Team = lazy(() => import("./pages/Team"));
const TeamMember = lazy(() => import("./pages/TeamMember"));
const Contact = lazy(() => import("./pages/Contact"));
const QuickContact = lazy(() => import("./pages/QuickContact"));
const Articles = lazy(() => import("./pages/Articles"));
const ArticleCategory = lazy(() => import("./pages/ArticleCategory"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const AccessibilityStatement = lazy(() => import("./pages/AccessibilityStatement"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const Index = lazy(() => import("./pages/Index"));
const ShopComingSoon = lazy(() => import("./pages/ShopComingSoon"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Reviews = lazy(() => import("./pages/Reviews"));
const PhysiotherapyBaqa = lazy(() => import("./pages/PhysiotherapyBaqa"));

// Admin (lazy — never loaded for public visitors)
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminReviews = lazy(() => import("./pages/AdminReviews"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminSettingsHub = lazy(() => import("./pages/admin/AdminSettingsHub"));
const AdminHero = lazy(() => import("./pages/admin/AdminHero"));
const AdminServices = lazy(() => import("./pages/admin/AdminServices"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminArticleEditor = lazy(() => import("./pages/admin/AdminArticleEditor"));
const AdminArticleCategories = lazy(() => import("./pages/admin/AdminArticleCategories"));
const AdminPageContent = lazy(() => import("./pages/admin/AdminPageContent"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminAboutContact = lazy(() => import("./pages/admin/AdminAboutContact"));
const AdminTestimonialVideos = lazy(() => import("./pages/admin/AdminTestimonialVideos"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminSmsTemplates = lazy(() => import("./pages/admin/AdminSmsTemplates"));
const AdminQuickContact = lazy(() => import("./pages/admin/AdminQuickContact"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<RouteSkeleton />}>
          <Routes>
            <Route path="/" element={<SplitterLanding />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="settings" element={<AdminSettingsHub />} />
              <Route path="settings/contact-info" element={<AdminSettings />} />
              <Route path="settings/quick-contact" element={<AdminQuickContact />} />
              <Route path="hero" element={<AdminHero />} />
              <Route path="services" element={<AdminServices />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="articles/categories" element={<AdminArticleCategories />} />
              <Route path="articles/new" element={<AdminArticleEditor />} />
              <Route path="articles/:id" element={<AdminArticleEditor />} />
              <Route path="pages" element={<AdminPageContent />} />
              <Route path="about-contact" element={<AdminAboutContact />} />
              <Route path="testimonial-videos" element={<AdminTestimonialVideos />} />
              <Route path="contacts" element={<AdminContacts />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="sms-templates" element={<AdminSmsTemplates />} />
              {/* Backward-compat redirect routes (old paths) */}
              <Route path="subject-areas" element={<AdminQuickContact />} />
            </Route>
            <Route element={<Layout />}>
              <Route path="/clinic" element={<Index />} />
              <Route path="/shop" element={<ShopComingSoon />} />
              <Route path="/services" element={<Services />} />
              <Route path="/physiotherapy-baqa-al-gharbiyye" element={<PhysiotherapyBaqa />} />
              <Route path="/about" element={<About />} />
              <Route path="/team" element={<Team />} />
              <Route path="/team/:slug" element={<TeamMember />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/articles/:categorySlug" element={<ArticleCategory />} />
              <Route path="/articles/:categorySlug/:articleSlug" element={<ArticleDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/contaform" element={<QuickContact />} />
              <Route path="/quick-contact" element={<QuickContact />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
      </LanguageProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
